import {
  FRCWidgetWidgetErrorEvent,
  FriendlyCaptchaSDK,
  WidgetHandle,
} from "@friendlycaptcha/sdk";
import { isTrulyVisible } from "@utils/typescript/helpers.ts";

const sitekey = "FCMPKP3O8NNFENKJ";
const friendlyCaptchaSDK = new FriendlyCaptchaSDK();

document.addEventListener("DOMContentLoaded", generateWidgets);

/*
 * Generating captcha widgets: If one widget is generated with a special attribute (e.g. a data-attribute), then all
 * all occurring instances of the widget will inherit the attribute.
 * If a captcha becomes visible with the IntersectionObserver, all widgets on the page are going to be initialized immediately.
 */

function generateWidgets() {
  const captchaWidgets: NodeListOf<HTMLDivElement> =
    document.querySelectorAll(".frc-captcha");

  captchaWidgets.forEach((element) => {
    // Init the captcha immediately even if not in view
    if (element.dataset.initImmediately !== null)
      return generateWidget(element);

    observeWidgetVisibility().observe(element);
  });
}

// Fallback for dynamically loaded widgets in flyouts/modals where IntersectionObserver might not work
document.addEventListener(
  "application.flyout.opened",
  (event) => {
    const widget: HTMLElement | null = (
      event.currentTarget as HTMLElement
    ).querySelector(".frc-captcha");

    if (widget) generateWidget(widget);
  },
  { once: true },
);

function generateWidget(widgetElement: HTMLElement) {
  if (widgetElement.dataset.initialized) return; // Prevent double initialization
  widgetElement.dataset.initialized = "true";

  const widget = friendlyCaptchaSDK.createWidget({
    element: widgetElement,
    sitekey,
    startMode: "focus",
  });

  widgetElement.classList.remove("frc-captcha"); // Remove the class to avoid double initialization
  widgetElement.classList.add("friendly-captcha"); // Add own class that is whitelisted in css-loader.css

  const form = widgetElement.closest("form");
  if (!form)
    return console.error("Captcha widget is not inside a form", widget);

  const submitButton: HTMLButtonElement | null = form.querySelector(
    "button[type=submit]",
  );
  if (!submitButton)
    return console.error("No submit button found for captcha widget", widget);

  widget.getElement().style.height = "40px"; // Set a lower height for the captcha
  widget.getElement().style.width = "100%"; // Set full width for the captcha
  widget
    .getElement()
    .querySelector("iframe")
    ?.setAttribute("title", "Friendly Captcha");

  submitButton.disabled = true; // Disable submit button until captcha is completed

  // Event listeners
  widget.addEventListener("frc:widget.complete", () => {
    submitButton.disabled = false;
    sendCaptchaCompleteSignal(); // Send signal that captcha is completed
  });
  widget.addEventListener("frc:widget.statechange", () => {
    sendCaptchaStateChangeSignal();
  });
  widget.addEventListener("frc:widget.error", (event) => {
    submitButton.disabled = true;
    logErrorAndResetCaptcha(widget, event);
  });

  widget.addEventListener("frc:widget.expire", () => widget.reset());

  // Reset the captcha when the email is validated
  document.addEventListener("ssi-email-validated", () => widget.reset());
  document.addEventListener("registration.reset-captcha", () => {
    widget.reset();
    widget.start();
  });

  window.addEventListener("pageshow", function (event) {
    // Reset the captcha if the page is loaded from bfcache (back-forward cache)
    if (event.persisted) {
      submitButton.disabled = true;
      widget.reset();
    }
  });
}

// Helpers
function logErrorAndResetCaptcha(
  widget: WidgetHandle,
  event: FRCWidgetWidgetErrorEvent,
) {
  widget.reset();

  const error = {
    location: window.location.href,
    error: event.detail,
  };

  console.error("Captcha error", error);
  fetch("/user/captcha/log", {
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
    body: JSON.stringify(error),
  });
}

function observeWidgetVisibility() {
  return new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && isTrulyVisible(entry.target)) {
        generateWidget(entry.target as HTMLElement);
        obs.unobserve(entry.target);
      }
    });
  });
}

// Send captcha complete signal
function sendCaptchaCompleteSignal() {
  // Info: We could listen to the "frc:widget.complete" event, but this is more generic and useful if friendly captcha is going to be replaced with another captcha solution in the future.
  document.dispatchEvent(new CustomEvent("ssi.captcha-complete"));
}

// Send captcha state change signal
function sendCaptchaStateChangeSignal() {
  document.dispatchEvent(new CustomEvent("ssi.captcha-statechange"));
}
